/**
 * Structured logging utilities for AI chat system with production observability
 */

import type { PostHog } from 'posthog-js';

import { getDeploymentEnvironment } from '@/lib/environment';
import { getErrorMessage } from '@/lib/utils';

// Dynamic imports for server-only functionality
// This prevents posthog-node from being bundled in client code
let captureServerException:
  ((error: Error, properties?: Record<string, unknown>) => Promise<void>) | null = null;

if (typeof window === 'undefined') {
  // Load PostHog
  import('@/lib/posthog-server')
    .then((module) => {
      captureServerException = module.captureServerException;
    })
    .catch((err) => {
      // Log failure but don't block - PostHog capture is optional
      // Early errors during cold start won't be captured to PostHog but will still go to console
      console.warn('Failed to load PostHog server module:', err);
    });
}

// Client-side PostHog (posthog-js). Mirror of the server pattern above:
// dynamic import so posthog-js never lands in server bundles. The imported
// default is the SAME singleton PostHogProvider initializes, so __loaded
// reflects real init state; errors logged before init (or when PostHog is
// unconfigured) degrade to console-only — the same cold-start tradeoff as
// the server import. (#2977: client logger.error previously reached only
// the browser console, so caught render crashes never hit error tracking.)
let clientPosthog: PostHog | null = null;

if (typeof window !== 'undefined') {
  import('posthog-js')
    .then((module) => {
      clientPosthog = module.default;
    })
    .catch((err) => {
      console.warn('Failed to load PostHog client module:', err);
    });
}

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/** Extra data to attach to a log entry (becomes the `data` field in JSON output) */
type LogExtra = unknown;

/** Data object that gets spread into log entries (must be an object for spreading) */
type LogData = Record<string, unknown>;

export interface LogContext {
  userId?: string | undefined;
  conversationId?: string | undefined;
  sessionId?: string | undefined;
  batchId?: string | undefined;
  requestId?: string | undefined;
  [key: string]: unknown;
}

interface PerformanceMetrics {
  duration: number;
  operation: string;
  success: boolean;
  errorType?: string | undefined;
  resourceUsage?:
    | {
        tokensUsed?: number | undefined;
        chunksProcessed?: number | undefined;
        embeddingsGenerated?: number | undefined;
      }
    | undefined;
}

interface CostMetrics {
  operation: 'embedding' | 'chat' | 'search';
  provider: 'openai' | 'supabase';
  tokensUsed?: number;
  estimatedCost?: number;
  model?: string;
}

/**
 * JSON replacer that serializes Error objects into plain objects.
 * Without this, JSON.stringify(new Error("boom")) produces "{}"
 * because Error properties (message, stack, name) are non-enumerable.
 */
export function errorReplacer(_key: string, value: unknown): unknown {
  if (value instanceof Error) {
    return {
      ...value,
      message: value.message,
      name: value.name,
      stack: value.stack,
      // Error.cause is non-enumerable too, so the spread drops it (#3550).
      // Re-adding it here lets JSON.stringify recurse into a wrapped root
      // cause with this same replacer. Conditional so cause-less Errors don't
      // gain a `cause: undefined` key. A circular cause chain (e.cause = e)
      // would recurse forever — deliberately unguarded: no runtime constructs
      // one, and enumerable self-references already threw before this change.
      ...(value.cause !== undefined ? { cause: value.cause } : {}),
    };
  }
  return value;
}

/**
 * Extracts message + structured fields (code/details/hint) from a Supabase
 * PostgrestError into discrete, enumerable string properties so they survive
 * posthog-node property serialization. A PostgrestError extends Error, whose
 * `message` is non-enumerable, so posthog-node otherwise flattens it to
 * `{"message":""}` — hiding the real Postgres error (e.g. 42703) in PostHog.
 *
 * Gated on the PostgrestError shape (`code` plus `details` or `hint`) so that
 * generic errors flowing through the same log path — caught exceptions, network
 * timeouts, Stripe/auth errors — don't get tagged with a misleading
 * `errorPgMessage`, which would pollute PostHog and make real Postgres errors
 * harder to filter. Only present, non-empty fields are included.
 */
export function extractPostgresErrorFields(error: unknown): Record<string, string> {
  const fields: Record<string, string> = {};
  if (!error || typeof error !== 'object') {
    return fields;
  }
  const e = error as { message?: unknown; code?: unknown; details?: unknown; hint?: unknown };
  const isPostgresError = 'code' in e && ('details' in e || 'hint' in e);
  if (!isPostgresError) {
    return fields;
  }
  if (typeof e.message === 'string' && e.message) fields.errorPgMessage = e.message;
  if (typeof e.code === 'string' && e.code) fields.errorCode = e.code;
  if (typeof e.details === 'string' && e.details) fields.errorDetails = e.details;
  if (typeof e.hint === 'string' && e.hint) fields.errorHint = e.hint;
  return fields;
}

/**
 * Read a `stack` string off a non-Error rejection. Third-party rejections (pdf.js,
 * SDK error-alikes) are often plain objects that still carry a real stack; keeping
 * it lets PostHog group by the true throw site instead of the logger's own frame.
 */
function extractErrorStack(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const stack = (error as { stack?: unknown }).stack;
  return typeof stack === 'string' && stack ? stack : undefined;
}

/**
 * The `Error.captureStackTrace` elision boundary: V8 drops every frame above AND
 * INCLUDING the function passed here, matched by identity rather than depth.
 *
 * Typed `(...args: never[]) => unknown` because two different shapes have to assign
 * to it — the `StructuredLogger.prototype.error` method for direct calls, and the
 * arrow `scope()` closes over for scoped calls. `never[]` makes the parameter list
 * contravariantly compatible with any signature, and `unknown` accepts any return.
 * (`Function` would work too but is banned by lint, and rightly: it erases arity.)
 */
type StackBoundary = (...args: never[]) => unknown;

class StructuredLogger {
  private isProduction = process.env.NODE_ENV === 'production';
  private pendingPostHogFlushes: Promise<void>[] = [];

  private formatMessage(level: LogLevel, message: string, context?: LogContext, extra?: LogExtra) {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level,
      message,
      service: 'loristry',
      environment: getDeploymentEnvironment(),
      ...(context ?? {}),
      ...(extra !== undefined && { data: extra }),
    };

    // In non-production (development/test), use pretty console output
    if (!this.isProduction) {
      const contextStr = context
        ? ` [${Object.entries(context)
            .map(([k, v]) => `${k}=${v}`)
            .join(', ')}]`
        : '';

      console[level === 'debug' ? 'log' : level](
        `${timestamp} ${level.toUpperCase()}${contextStr}: ${message}`,
        extra || ''
      );
    } else {
      // In production, send structured JSON to stdout for Vercel log ingestion
      const consoleMethod =
        level === 'error' ? console.error : level === 'warn' ? console.warn : console.log;
      consoleMethod(JSON.stringify(logEntry, errorReplacer));
    }
  }

  async flush() {
    // Capture pending PostHog flushes atomically to avoid race condition
    // (if new errors are logged during flush, they go into a fresh array)
    const pendingFlushes = this.pendingPostHogFlushes;
    this.pendingPostHogFlushes = [];
    await Promise.all(pendingFlushes);
  }

  /**
   * Determines if an object looks like extra data (only has 'error' key) vs context
   * Objects with error + other keys (like jobId, userId) are treated as context
   * to preserve observability metadata
   */
  private isExtraData(obj: unknown): obj is { error: unknown } {
    if (!obj || typeof obj !== 'object' || !('error' in obj)) {
      return false;
    }
    // Only treat as extra if error is the sole key
    const keys = Object.keys(obj);
    return keys.length === 1 && keys[0] === 'error';
  }

  /**
   * Normalizes arguments to handle both:
   * - logger.method('msg', { error }) - 2 args, second is extra
   * - logger.method('msg', { userId }, { error }) - 3 args
   */
  private normalizeArgs(
    contextOrExtra?: LogContext,
    extra?: LogExtra
  ): [LogContext | undefined, LogExtra] {
    if (extra !== undefined) {
      return [contextOrExtra, extra];
    }
    if (this.isExtraData(contextOrExtra)) {
      return [undefined, contextOrExtra];
    }
    return [contextOrExtra, undefined];
  }

  debug(message: string, contextOrExtra?: LogContext, extra?: LogExtra) {
    const [context, extraData] = this.normalizeArgs(contextOrExtra, extra);
    this.formatMessage('debug', message, context, extraData);
  }

  info(message: string, contextOrExtra?: LogContext, extra?: LogExtra) {
    const [context, extraData] = this.normalizeArgs(contextOrExtra, extra);
    this.formatMessage('info', message, context, extraData);
  }

  warn(message: string, contextOrExtra?: LogContext, extra?: LogExtra) {
    const [context, extraData] = this.normalizeArgs(contextOrExtra, extra);
    this.formatMessage('warn', message, context, extraData);
  }

  /**
   * Derive the Error instance + PostHog properties for an error() call. Always
   * returns a capture: when the call carries no error payload, the Error is
   * synthesized from the log line (#3149). Shared by the server and
   * client sinks so both capture the same shapes:
   *  - extra.error that IS an Error (recommended pattern)
   *  - extra.error that is a string — the sole-key `{ error: getErrorMessage(e) }`
   *    shape previously captured NOTHING on either runtime (#2977 incident shape)
   *  - context.error (Error | string | object) and/or context.stack (legacy)
   */
  private deriveExceptionCapture(
    message: string,
    context: LogContext | undefined,
    extraData: LogExtra,
    stackBoundary: StackBoundary
  ): { errorInstance: Error; properties: Record<string, unknown> } {
    const extraError =
      extraData && typeof extraData === 'object' && 'error' in extraData
        ? (extraData as { error: unknown }).error
        : undefined;
    const rawError = extraError !== undefined ? extraError : context?.error;
    const contextStack = typeof context?.stack === 'string' ? context.stack : undefined;

    // No early return on a missing payload (#3149). An error-level log is a defect by
    // construction — #3085 routes expected user errors to logger.warn, which has no
    // PostHog sink — so `error` must always reach error tracking. The old
    // `if (!rawError && !contextStack) return null` silently downgraded to console-only
    // any payload filed under a key other than `error`/`stack`, on BOTH runtimes.
    // When nothing is derivable the branch below synthesizes new Error(message),
    // because getErrorMessage(undefined, message) falls through to its fallback and
    // returns `message`.
    let errorInstance: Error;
    if (rawError instanceof Error) {
      errorInstance = rawError;
    } else {
      // getErrorMessage reads `message` off plain-object rejections (Supabase and
      // API errors) as well as strings; deriving from the log line instead would
      // group every such failure under one generic exception. The `|| message`
      // guard keeps an empty message falling back to the log line.
      errorInstance = new Error(getErrorMessage(rawError, message) || message);
      // An explicit context.stack wins; otherwise salvage any stack the rejection
      // carried, so the issue shows the real throw site and not this line (the
      // browser also groups by it; the server keys on the log line, #5056).
      const rawStack = contextStack ?? extractErrorStack(rawError);
      if (rawStack) {
        errorInstance.stack = rawStack;
      } else if (typeof Error.captureStackTrace === 'function') {
        // Nothing to salvage, so the synthesized Error's stack is topped by the
        // logger's own frames — which would make every synthesized exception read
        // as thrown inside the logger. Elide them so the culprit is the caller
        // (#3149). `stackBoundary` is the PUBLIC entry point the caller invoked, and
        // it differs by path: `error()` for a direct call, `scope()`'s arrow for a
        // scoped one. Hardcoding `prototype.error` left the arrow on top and
        // attributed ~285 of 890 call sites to logger.ts. V8-only: posthog-js runs
        // in Safari/Firefox, where this is undefined and the untrimmed stack is the
        // graceful fallback.
        Error.captureStackTrace(errorInstance, stackBoundary);
      }
    }

    // Strip the keys we just consumed — the Error argument passed alongside these
    // properties is already the canonical source of message/stack, so re-spreading
    // them would store redundant custom properties (and `error` serializes to a
    // useless `{}`, since Error fields are non-enumerable). Mirrors the identity-key
    // strip in captureServerException.
    const { error: _consumedError, stack: _consumedStack, ...restContext } = context ?? {};

    return {
      errorInstance,
      properties: {
        ...restContext,
        ...extractPostgresErrorFields(rawError),
        errorMessage: message,
      },
    };
  }

  /**
   * Log an error and capture it to PostHog error tracking (posthog-node on
   * the server, posthog-js in the browser).
   *
   * Pass the error ITSELF under `error`, in either the context or the extra argument:
   * ```typescript
   * logger.error('Request failed', { requestId: '123' }, { error });
   * logger.error('Request failed', { error, requestId: '123' });
   * ```
   * Never `{ error: error.message }`: the string forces a synthesized Error, losing the
   * original type, throw-site stack, and Supabase code/hint/details breakout. Pinned by
   * logger-error-payload-key.test.ts.
   *
   * On the server the capture promise is tracked so flush() can await it before
   * serverless termination; the client sink is fire-and-forget (see below).
   *
   * @param stackBoundary INTERNAL — do not pass from application code. It names the
   *   frame `Error.captureStackTrace` elides down to when a stack has to be
   *   synthesized, and exists solely so `scope()` can nominate its own arrow instead
   *   of this method (see `scope()` for why the default is wrong there). Defaulted,
   *   so every ordinary three-argument call is unaffected.
   *
   *   This is deliberately a fourth parameter on the PUBLIC method rather than a
   *   private overload the scoped arrow calls directly: `error()` must stay the single
   *   funnel every scoped call passes through, because tests spy on it
   *   (`jest.spyOn(logger, 'error')`) to intercept scoped logging. Bypassing it made
   *   `expect(logger.error).not.toHaveBeenCalled()` pass VACUOUSLY at every scoped call
   *   site — a silently-green assertion, which is worse than the extra parameter.
   */
  error(
    message: string,
    contextOrExtra?: LogContext,
    extra?: LogExtra,
    stackBoundary: StackBoundary = StructuredLogger.prototype.error
  ) {
    const [context, extraData] = this.normalizeArgs(contextOrExtra, extra);
    this.formatMessage('error', message, context, extraData);

    const derived = this.deriveExceptionCapture(message, context, extraData, stackBoundary);

    // Server sink: posthog-node via captureServerException. The promise is
    // tracked so flush() can await it before serverless termination.
    if (captureServerException) {
      const promise = captureServerException(derived.errorInstance, derived.properties).catch(
        (err) => {
          console.error('Failed to capture exception to PostHog:', err);
        }
      );
      this.pendingPostHogFlushes.push(promise);
      return;
    }

    // Client sink: posthog-js captureException (#2977). Guards:
    //  - __loaded: PostHogProvider init runs in a mount effect; pre-init
    //    errors stay console-only (same cold-start tradeoff as the imports).
    //  - NODE_ENV: mirrors capture_exceptions: !isDevelopment (posthog.tsx)
    //    and the dev skip in getPostHogServer(). Not this.isProduction, so
    //    staging/preview (NODE_ENV=production) capture and Jest
    //    (NODE_ENV=test) can exercise this path. In real client bundles the
    //    expression is inlined at build time by webpack; only Jest flips it
    //    at runtime.
    // Fire-and-forget: posthog-js batches internally and flushes via
    // sendBeacon on unload, so no flush() tracking is needed here.
    if (clientPosthog?.__loaded && process.env.NODE_ENV !== 'development') {
      try {
        clientPosthog.captureException(derived.errorInstance, {
          // Default source; context can override (e.g. 'error_boundary')
          // because derived.properties spreads after it.
          source: 'client_logger',
          ...derived.properties,
        });
      } catch (err) {
        console.error('Failed to capture exception to PostHog:', err);
      }
    }
  }

  /**
   * Log performance metrics for monitoring and alerting
   */
  performance(metrics: PerformanceMetrics, context?: LogContext) {
    this.info('Performance metric recorded', context, {
      type: 'performance_metric',
      ...metrics,
    });
  }

  /**
   * Log cost metrics for billing and usage tracking
   */
  cost(metrics: CostMetrics, context?: LogContext) {
    this.info('Cost metric recorded', context, {
      type: 'cost_metric',
      ...metrics,
    });
  }

  /**
   * Log user interactions for analytics
   */
  userInteraction(action: string, context: LogContext, data?: LogData) {
    this.info(`User interaction: ${action}`, context, {
      type: 'user_interaction',
      action,
      ...data,
    });
  }

  /**
   * Log system events for monitoring
   */
  systemEvent(
    event: string,
    status: 'success' | 'failure' | 'warning',
    context?: LogContext,
    data?: LogData
  ) {
    const level = status === 'failure' ? 'error' : status === 'warning' ? 'warn' : 'info';
    this.formatMessage(level, `System event: ${event}`, context, {
      type: 'system_event',
      event,
      status,
      ...data,
    });
  }

  /**
   * Create a scoped logger with persistent context
   */
  scope(context: LogContext) {
    // Named + self-referential so it can nominate ITSELF as the stack-elision
    // boundary. A scoped call stacks as deriveExceptionCapture → error → THIS ARROW →
    // caller, so `error()`'s default boundary elides one frame too few: it stops at
    // `error`, leaving the arrow on top and blaming logger.ts for every scoped
    // synthesized exception. Note this still goes THROUGH `this.error`, which spies
    // rely on — see the note on the `stackBoundary` parameter. The explicit type
    // annotation breaks the inference cycle the self-reference would otherwise create.
    const scopedError: (message: string, extra?: LogExtra) => void = (message, extra) =>
      this.error(message, context, extra, scopedError);

    return {
      debug: (message: string, extra?: LogExtra) => this.debug(message, context, extra),
      info: (message: string, extra?: LogExtra) => this.info(message, context, extra),
      warn: (message: string, extra?: LogExtra) => this.warn(message, context, extra),
      error: scopedError,
      performance: (metrics: PerformanceMetrics) => this.performance(metrics, context),
      cost: (metrics: CostMetrics) => this.cost(metrics, context),
      userInteraction: (action: string, data?: LogData) =>
        this.userInteraction(action, context, data),
      systemEvent: (event: string, status: 'success' | 'failure' | 'warning', data?: LogData) =>
        this.systemEvent(event, status, context, data),
    };
  }
}

// Global logger instance
export const logger = new StructuredLogger();

/**
 * Type for the scoped logger returned by logger.scope()
 */
export type ScopedLogger = ReturnType<typeof logger.scope>;

/**
 * Performance timing utility
 */
export class PerformanceTimer {
  private startTime: number;
  private operation: string;
  private context: LogContext;

  constructor(operation: string, context: LogContext = {}) {
    this.startTime = Date.now();
    this.operation = operation;
    this.context = context;

    logger.debug(`Starting operation: ${operation}`, context);
  }

  end(
    success: boolean = true,
    errorType?: string,
    resourceUsage?: PerformanceMetrics['resourceUsage']
  ) {
    const duration = Date.now() - this.startTime;

    const metrics: PerformanceMetrics = {
      duration,
      operation: this.operation,
      success,
      errorType,
      resourceUsage,
    };

    logger.performance(metrics, this.context);

    const status = success ? 'completed' : 'failed';
    const message = `Operation ${this.operation} ${status} in ${duration}ms`;

    if (success) {
      logger.info(message, this.context);
    } else {
      logger.error(message, this.context, { errorType });
    }

    return metrics;
  }
}
